import React, { useMemo } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useSearchParams, useLocation } from 'react-router-dom'
import { YStack, XStack } from 'tamagui'

import { Metadata } from '@newComponents/Metadata'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'

import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Card } from '@newComponents/Card'
import { H2, Paragraph, Text } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { SignInScreenContent } from './SignInScreenContent'

export const SignInScreen = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const signInMethod = useMemo<'email' | 'password'>(() => {
    const method = searchParams.get('method')

    return ['email', 'password'].includes(method || '')
      ? (method as 'email' | 'password')
      : 'email'
  }, [searchParams.get('method')])

  const routerState = useMemo(
    () => ({
      state: {
        backgroundLocation: location.state?.backgroundLocation as
          | Location
          | undefined,
        authRedirectPath:
          (location.state?.authRedirectPath as string) || undefined,
      },
      replace: true,
    }),
    [location]
  )

  return (
    <>
      <Metadata title='Sign In' />

      <GradientBackgroundScreen alignItems='center'>
        <Card
          gap='$6'
          maxWidth={400}
          width='100%'
          padding='$0'
          borderRadius='$8'
        >
          <XStack
            gap='$6'
            alignItems='center'
            paddingHorizontal='$8'
            paddingTop='$7'
          >
            <EmojiIcon size='$8' name='waving-hand' />

            <YStack gap='$1' flex={1}>
              <H2>Sign in</H2>

              {signInMethod === 'email' ? (
                <Text size='$4' fontWeight='$3' color='$colorMuted'>
                  We'll send you a code securely via email
                </Text>
              ) : (
                <Text size='$4' fontWeight='$3' color='$colorMuted'>
                  Enter your email and password below
                </Text>
              )}
            </YStack>
          </XStack>

          <SignInScreenContent />

          <YStack
            backgroundColor='$accentSurface'
            padding='$4'
            borderRadius={0}
            borderBottomLeftRadius='$8'
            borderBottomRightRadius='$8'
            alignItems='center'
            flex={1}
            width='100%'
          >
            {signInMethod === 'email' ? (
              <XStack gap='$2'>
                <Text size='$4' color='$colorOnPrimary'>
                  Prefer a password?
                </Text>

                <Link
                  color='$colorOnPrimary'
                  size='$4'
                  textDecorationLine='underline'
                  href='/sign-in?method=password'
                  options={routerState}
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                >
                  Use a password instead
                </Link>
              </XStack>
            ) : (
              <XStack gap='$2'>
                <Text size='$4' color='$colorOnPrimary'>
                  Prefer an email?
                </Text>

                <Link
                  color='$colorOnPrimary'
                  size='$4'
                  textDecorationLine='underline'
                  href='/sign-in?method=email'
                  options={routerState}
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                >
                  Use an email instead
                </Link>
              </XStack>
            )}
          </YStack>
        </Card>
      </GradientBackgroundScreen>
    </>
  )
}

export default SignInScreen
